<template>
  <transition name="modal-fade">
    <div v-if="show" class="confirmation-modal" @click.self="$emit('cancel')">
      <div class="confirmation-content">
        <div class="confirmation-header">
          <i class="fa-solid fa-trash"></i>
          <h3>Delete Team</h3>
        </div>
        <div class="confirmation-body">
          <p>Are you sure you want to delete <strong>{{ team.name }}</strong>?</p>
          <p class="warning-text">This cannot be undone. All members, invites, and team data will be permanently removed.</p>
          <div class="booking-info">
            <p><strong>Team ID:</strong> {{ team.team_id }}</p>
            <p><strong>Members:</strong> {{ team.member_count }}</p>
            <p><strong>Matches:</strong> {{ team.total_matches }}</p>
          </div>
          <label class="confirm-check">
            <input v-model="acknowledgeRisk" type="checkbox">
            <span>I understand this will permanently delete my team and related data.</span>
          </label>
          <label class="confirm-label" for="delete-team-name-confirm">Type the team name to confirm</label>
          <input
            id="delete-team-name-confirm"
            v-model.trim="confirmName"
            type="text"
            class="confirm-input"
            :placeholder="team.name"
            autocomplete="off"
          >
        </div>
        <div class="confirmation-actions">
          <button class="btn-cancel" @click="$emit('cancel')">
            <i class="fa-solid fa-times"></i>
            Cancel
          </button>
          <button class="btn-confirm" :disabled="!canConfirmDelete" @click="onConfirm">
            <i class="fa-solid fa-trash"></i>
            Delete Team
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'DeleteConfirmModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    team: {
      type: Object,
      required: true
    }
  },
  emits: ['cancel', 'confirm'],
  data() {
    return {
      acknowledgeRisk: false,
      confirmName: ''
    };
  },
  computed: {
    canConfirmDelete() {
      const expected = (this.team?.name || '').trim().toLowerCase();
      if (!expected || !this.acknowledgeRisk) return false;
      return this.confirmName.trim().toLowerCase() === expected;
    }
  },
  watch: {
    show(val) {
      if (!val) {
        this.acknowledgeRisk = false;
        this.confirmName = '';
      }
    }
  },
  methods: {
    onConfirm() {
      if (!this.canConfirmDelete) return;
      this.$emit('confirm');
    }
  }
};
</script>

<style scoped>
.confirmation-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--light-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(10px);
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.confirmation-content {
  background: var(--light-card);
  border-radius: 20px;
  max-width: 400px;
  width: 90%;
  overflow: hidden;
  animation: modalScale 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.confirmation-header {
  padding: 30px 30px 20px;
  text-align: center;
}

.confirmation-header i {
  font-size: 3rem;
  margin-bottom: 15px;
  display: block;
  color: var(--section-icon);
}

.confirmation-header h3 {
  margin: 0;
  color: var(--heading-color);
}

.confirmation-body {
  padding: 0 30px 30px;
  text-align: center;
}

.confirmation-body p {
  color: var(--subtext-color);
  line-height: 1.6;
  margin-bottom: 15px;
}

.warning-text {
  color: var(--rejected-color) !important;
  font-weight: 600;
}

.booking-info {
  background: var(--meta-bg);
  padding: 15px;
  border-radius: 12px;
  margin-top: 20px;
  text-align: left;
}

.booking-info p {
  margin: 5px 0;
  color: var(--subtext-color);
}

.booking-info strong {
  color: var(--heading-color);
}

.confirm-check {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  text-align: left;
  margin-top: 18px;
  font-size: 0.9rem;
  color: var(--subtext-color);
  cursor: pointer;
}

.confirm-check input {
  margin-top: 3px;
  flex-shrink: 0;
}

.confirm-label {
  display: block;
  margin-top: 16px;
  margin-bottom: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--heading-color);
  text-align: left;
}

.confirm-input {
  width: 100%;
  box-sizing: border-box;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(0, 0, 0, 0.2);
  color: var(--heading-color);
  font-size: 0.95rem;
}

.btn-confirm:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.confirmation-actions {
  display: flex;
  gap: 10px;
  padding: 20px 30px;
  background: rgba(0, 0, 0, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.confirmation-actions button {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-confirm {
  background: var(--rejected-bg);
  color: var(--rejected-color);
  border: 1px solid var(--rejected-border);
}

.btn-cancel {
  background: rgba(255, 255, 255, 0.1);
  color: var(--subtext-color);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(245, 101, 101, 0.3);
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.2);
  color: var(--heading-color);
}

@keyframes modalScale {
  from { transform: scale(0.9); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}
</style>
